<template>
  <div ref="input" :class="containerClassName">
    <vs-input
      v-bind="$attrs"
      v-model="inputValue"
      @focus="onInputFocus"
      @blur="onInputBlur"
      @keydown="onInputKeydown"
      @click="onInputClick"
      :placeholder="placeholder"
      :class="[$style.input, inputClass]"
      :disabled="disabled"
    />
    <button
      v-if="clearable && inputValue && !disabled"
      @click.capture.stop="onSelectDate(null)"
      type="button"
      :class="$style.clear"
      tabindex="-1"
    >
      <app-icon name="XIcon" size="8px" />
    </button>
    <mounting-portal v-if="isFocused" mount-to="body" append>
      <div ref="datepicker" :class="$style.datepickerWrapper">
        <v-datepicker
          :class="$style.datapicker"
          :inline="true"
          :value="datepickerDate"
          :language="datepickerLanguage"
          acceptable
          @input="onSelectDate"
          v-bind="datepickerProps"
          :monday-first="true"
        />
      </div>
    </mounting-portal>
  </div>
</template>

<script>
import { createPopper } from '@popperjs/core';
import dateRU           from "date-fns/locale/ru";
import dateParse        from 'date-fns/parse';
import startOfDay       from 'date-fns/startOfDay';
import formatDate       from "@/client/helpers/formatDate";
import { ru }           from 'vuejs-datepicker/dist/locale';
import VDatepicker      from 'vuejs-datepicker';

const DEFAULT_DATE_FORMAT = 'dd.MM.yyyy';
const POPPER_OPTIONS = {
  placement: "bottom-start",
  modifiers: [
    // { name: 'offset', options: { offset: [0, 6] } },
    // { name: 'arrow', options: { padding: 20 } },
  ],
};

export default {
  name:       "ElsaVDatepicker",
  components: { VDatepicker },
  data() {
    return {
      isFocused:          false,
      datepickerDate:     this.value || null,
      datepickerLanguage: ru,
      inputValue:         '',
      popper:             null,
    };
  },
  props:    {
    value:           {},
    viewFormat:      {},
    datepickerProps: {},
    placeholder:     {},
    inputClass:      {},
    containerClass:  {},
    disabled:        {},
    styleType:       {
      type: [String, Array],
    },
    clearable:       {
      default: true,
    },
  },
  computed: {
    containerClassName() {
      const styleTypes = Array.isArray(this.styleType) ? this.styleType : [this.styleType];

      return {
        [this.$style.container]: true,
        [this.containerClass]:   !!this.containerClass,
        [this.$style.small]:     styleTypes.includes('small'),
      };
    },
  },
  beforeDestroy() {
    this.destroyPopper();
  },
  methods: {
    updateInputValue( date ) {
      this.inputValue = (date && !Number.isNaN(+date))
        ? formatDate(date, this.viewFormat || DEFAULT_DATE_FORMAT)
        : '';
    },
    onInputFocus( e ) {
      this.isFocused = true;
      this.$emit('focus', e);
    },
    onInputClick( e ) {
      this.isFocused = true;
      this.$emit('click', e);
    },
    onInputBlur( e ) {
      this.isFocused = false;
      this.updateInputValue(this.datepickerDate);
      this.$emit('blur', e);
      if (+this.datepickerDate !== +this.value) this.$emit('input', this.datepickerDate);
    },
    onInputKeydown( e ) {
      if (e.keyCode === 27) { // ESC
        this.isFocused = false;
      } else {
        this.isFocused = true;
      }
      this.$emit('keydown', e);
    },
    onSelectDate( date ) {
      date = date ? startOfDay(date) : null;
      this.datepickerDate = date;
      this.updateInputValue(date);
      if (+this.value !== +date) this.$emit('input', date);

      this.isFocused = false;
    },
    createPopper() {
      this.destroyPopper();
      const { input, datepicker } = this.$refs;
      this.popper = createPopper(input, datepicker, POPPER_OPTIONS);
    },
    destroyPopper() {
      if (this.popper) {
        this.popper.destroy();
        this.popper = null;
      }
    },
  },
  mounted() {
    this.updateInputValue(this.datepickerDate);
  },
  watch: {
    isFocused( isFocused ) {
      if (isFocused) {
        this.$nextTick(this.createPopper);
      } else {
        this.destroyPopper();
      }
    },
    value( value ) {
      if (+value === +this.datepickerDate) return;
      this.datepickerDate = value;
      this.updateInputValue(value);
    },
    inputValue( value ) {
      const date = dateParse(value, this.viewFormat || DEFAULT_DATE_FORMAT, new Date(), { locale: dateRU });
      this.datepickerDate = Number.isNaN(+date) ? null : date;
      // this.$emit('input', finalValue);
    },
  },
};
</script>

<style module lang="scss">
.container {
  position: relative;

  .input {
    width: 100%;
  }
}

.clear {
  position: absolute;
  border: 0;
  margin: 0;
  padding: 0;
  right: 8px;
  bottom: 11px;
  cursor: pointer;
  background: transparent;
}

.small {
  :global {
    .vs-con-input .vs-input--input {
      //font-size: .95rem;
      padding: 7px;
    }
  }

  .clear {
    bottom: 9px;
  }
}

.datepickerWrapper {
  position: absolute;
  z-index: 1000001;
}

.datapicker:global(.vdp-datepicker) {

}
</style>
